import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash/get';
import axiosConfig from './axiosConfig';
import { Notification } from '../../components/ui/custom-notification';
import {
  SAVE_SPOOL_FIT_UP,
  SAVE_SPOOL_PREHEAT,
  SAVE_SPOOL_WELDING,
  SAVE_SPOOL_VT,
  SAVE_SPOOL_NDT,
  SAVE_SPOOL_COATING,
  SAVE_SPOOL_ACCEPT,
  FETCH_ALL_SPOOLS,
  REGISTER_SPOOL_NCR,
  FETCH_SPOOL_NCR_LIST,
  FETCH_PROJECT_SPOOL_DASHBOARD,
} from './types';
import {
  saveSpoolFitUpSuccess,
  saveSpoolFitUpFailure,
  saveSpoolPreheatSuccess,
  saveSpoolPreheatFailure,
  saveSpoolWeldingSuccess,
  saveSpoolWeldingFailure,
  saveSpoolVtSuccess,
  saveSpoolVtFailure,
  saveSpoolNdtSuccess,
  saveSpoolNdtFailure,
  saveSpoolCoatingSuccess,
  saveSpoolCoatingFailure,
  saveSpoolAcceptSuccess,
  saveSpoolAcceptFailure,
  fetchAllSpools,
  fetchAllSpoolsSuccess,
  fetchAllSpoolsFailure,
  registerSpoolNcrSuccess,
  registerSpoolNcrFailure,
  fetchSpoolNcrList,
  fetchSpoolNcrListSuccess,
  fetchSpoolNcrListFailure,
  fetchProjectSpoolDashboardSuccess,
  fetchProjectSpoolDashboardFailure,
} from './actions';
import { mapGetAllSpoolsResponse } from './mapGetAllSpoolsResponse';
import { fetchSpoolNcrList as getSpoolNcrListRequest } from '../../features/project/components/dashboard/spooltracker/spoolNcrApi';

/**
 * Relative paths under `REACT_APP_BASE_URL` (see axiosConfig).
 * Align with backend when contracts exist; payloads are the panel DTOs as-is.
 */
const GET_ALL_SPOOLS_PATH = '/spool-tracker/getAllSpools';
const PROJECT_SPOOL_DASHBOARD_PATH = '/spool-tracker/project-dashboard';

const WELD_STEP_PATH = {
  fitUp: '/spool-tracker/weld-steps/fit-up',
  preheat: '/spool-tracker/weld-steps/preheat',
  welding: '/spool-tracker/weld-steps/welding',
  vt: '/spool-tracker/weld-steps/vt',
  ndt: '/spool-tracker/weld-steps/ndt',
  coating: '/spool-tracker/weld-steps/coating',
  accept: '/spool-tracker/weld-steps/accept',
};

/** Must match `SPOOL_NCR_REGISTER_PATH` in `spoolNcrApi.ts`. */
const SPOOL_NCR_REGISTER_PATH = '/spool-tracker/ncr/register';

function* fetchSpoolNcrListSaga(action) {
  const p = action?.payload;
  const payload = p && typeof p === 'object' ? p : {};
  try {
    const projectId = payload?.projectId;
    const pipeId = payload?.pipeId;
    if (projectId == null || projectId === '' || pipeId == null || pipeId === '') {
      yield put(fetchSpoolNcrListSuccess([]));
      return;
    }
    const rows = yield call(getSpoolNcrListRequest, {
      projectId,
      pipeId,
      spoolId: payload.spoolId != null ? payload.spoolId : undefined,
    });
    yield put(fetchSpoolNcrListSuccess(Array.isArray(rows) ? rows : []));
  } catch (error) {
    yield put(fetchSpoolNcrListFailure(error));
  }
}

function* postStep(path, payload) {
  const res = yield call([axiosConfig, axiosConfig.post], path, payload);
  return get(res, ['data', 'data'], get(res, ['data'], res.data));
}

function* fetchProjectSpoolDashboardSaga({ payload }) {
  const p = payload && typeof payload === 'object' ? payload : {};
  try {
    const projectId = p.projectId;
    if (projectId == null || projectId === '') {
      yield put(fetchProjectSpoolDashboardFailure(new Error('projectId is required')));
      return;
    }
    const params = { project_id: projectId };
    if (p.pipeId != null && p.pipeId !== '') {
      params.pipe_id = p.pipeId;
    }
    const res = yield call([axiosConfig, axiosConfig.get], PROJECT_SPOOL_DASHBOARD_PATH, { params });
    const inner = get(res, ['data', 'data'], get(res, ['data'], null));
    yield put(fetchProjectSpoolDashboardSuccess(inner && typeof inner === 'object' ? inner : {}));
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'Failed to load project dashboard';
    yield put(fetchProjectSpoolDashboardFailure(error));
    Notification.error('Dashboard', String(msg));
  }
}

function* fetchAllSpoolsSaga({ payload }) {
  try {
    const projectId = payload?.projectId;
    const pipeId = payload?.pipeId;
    if (projectId == null || projectId === '' || pipeId == null || pipeId === '') {
      yield put(fetchAllSpoolsSuccess([]));
      return;
    }
    const res = yield call([axiosConfig, axiosConfig.get], GET_ALL_SPOOLS_PATH, {
      params: { project_id: projectId, pipe_id: pipeId },
    });
    const body = get(res, ['data'], res.data);
    const list = mapGetAllSpoolsResponse(body);
    yield put(fetchAllSpoolsSuccess(list));
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'Failed to load spools';
    yield put(fetchAllSpoolsFailure(error));
    Notification.error('Spools', String(msg));
  }
}

/** Refresh grid after a weld-step save when `projectId` / `pipeId` are on the save payload. */
function* refetchSpoolsAfterSave(payload) {
  const projectId = payload?.projectId;
  const pipeId = payload?.pipeId;
  if (projectId == null || projectId === '' || pipeId == null || pipeId === '') return;
  yield put(fetchAllSpools({ projectId, pipeId }));
}

function* saveFitUpSaga({ payload }) {
  try {
    const data = yield call(postStep, WELD_STEP_PATH.fitUp, payload);
    yield put(saveSpoolFitUpSuccess({ response: data, request: payload }));
    yield call(refetchSpoolsAfterSave, payload);
    Notification.success('Fit-up saved', `${payload?.weldNumber ?? '—'}`);
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'Fit-up save failed';
    yield put(saveSpoolFitUpFailure(error));
    Notification.error('Fit-up save failed', String(msg));
  }
}

function* savePreheatSaga({ payload }) {
  try {
    const data = yield call(postStep, WELD_STEP_PATH.preheat, payload);
    yield put(saveSpoolPreheatSuccess({ response: data, request: payload }));
    yield call(refetchSpoolsAfterSave, payload);
    Notification.success('Preheat saved', `${payload?.weldNumber ?? '—'}`);
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'Preheat save failed';
    yield put(saveSpoolPreheatFailure(error));
    Notification.error('Preheat save failed', String(msg));
  }
}

function* saveWeldingSaga({ payload }) {
  try {
    const data = yield call(postStep, WELD_STEP_PATH.welding, payload);
    yield put(saveSpoolWeldingSuccess({ response: data, request: payload }));
    yield call(refetchSpoolsAfterSave, payload);
    const isRepair =
      payload?.weldingStepKind === 'repair_weld' ||
      payload?.repairLink != null ||
      (Array.isArray(payload?.repairsDraft) && payload.repairsDraft.length > 0);
    Notification.success(
      isRepair ? 'Repair weld saved' : 'Welding record saved',
      `${payload?.weldNumberInput || payload?.weldNumber || '—'}`
    );
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'Welding save failed';
    yield put(saveSpoolWeldingFailure(error));
    Notification.error('Welding save failed', String(msg));
  }
}

function* saveVtSaga({ payload }) {
  try {
    const data = yield call(postStep, WELD_STEP_PATH.vt, payload);
    yield put(saveSpoolVtSuccess({ response: data, request: payload }));
    yield call(refetchSpoolsAfterSave, payload);
    Notification.success('VT saved', `${payload?.weldNumber ?? '—'}`);
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'VT save failed';
    yield put(saveSpoolVtFailure(error));
    Notification.error('VT save failed', String(msg));
  }
}

function* saveNdtSaga({ payload }) {
  try {
    const data = yield call(postStep, WELD_STEP_PATH.ndt, payload);
    yield put(saveSpoolNdtSuccess({ response: data, request: payload }));
    yield call(refetchSpoolsAfterSave, payload);
    Notification.success('NDT saved', `${payload?.reportNumber || '—'}`);
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'NDT save failed';
    yield put(saveSpoolNdtFailure(error));
    Notification.error('NDT save failed', String(msg));
  }
}

function* saveCoatingSaga({ payload }) {
  try {
    const data = yield call(postStep, WELD_STEP_PATH.coating, payload);
    yield put(saveSpoolCoatingSuccess({ response: data, request: payload }));
    yield call(refetchSpoolsAfterSave, payload);
    Notification.success('Coating saved', `${payload?.coatingBatchNumber || '—'}`);
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'Coating save failed';
    yield put(saveSpoolCoatingFailure(error));
    Notification.error('Coating save failed', String(msg));
  }
}

function* saveAcceptSaga({ payload }) {
  try {
    const data = yield call(postStep, WELD_STEP_PATH.accept, payload);
    yield put(saveSpoolAcceptSuccess({ response: data, request: payload }));
    yield call(refetchSpoolsAfterSave, payload);
    if (payload?.mode === 'draft') {
      Notification.success('Acceptance draft saved', `${payload?.spoolName ?? '—'}`);
    } else {
      Notification.success('Acceptance saved', `${payload?.spoolName ?? '—'}`);
    }
  } catch (error) {
    const msg = get(error, 'message') || get(error, 'name') || 'Accept save failed';
    yield put(saveSpoolAcceptFailure(error));
    Notification.error('Accept save failed', String(msg));
  }
}

function* registerSpoolNcrSaga({ payload }) {
  try {
    const data = yield call(postStep, SPOOL_NCR_REGISTER_PATH, payload);
    yield put(registerSpoolNcrSuccess({ response: data, request: payload }));
    const pid = payload?.projectId;
    const poid = payload?.pipeId;
    if (pid != null && pid !== '' && poid != null && poid !== '') {
      yield put(
        fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: payload.spoolId != null ? payload.spoolId : undefined })
      );
    }
    yield call(refetchSpoolsAfterSave, payload);
    const ncr = payload?.ncrNumber || payload?.ncr_number;
    if (payload?.submissionMode === 'draft') {
      Notification.success('NCR', ncr ? `Draft saved — ${ncr}` : 'NCR draft saved.');
    } else {
      Notification.success('NCR', ncr ? `Submitted — ${ncr}` : 'NCR submitted.');
    }
  } catch (error) {
    const msg =
      get(error, 'response.data.message') ||
      get(error, 'response.data.name') ||
      get(error, 'message') ||
      get(error, 'name') ||
      'NCR register failed';
    yield put(registerSpoolNcrFailure(error));
    Notification.error('NCR', String(msg));
  }
}

export default function* spoolsbaseSaga() {
  yield takeLatest(FETCH_PROJECT_SPOOL_DASHBOARD, fetchProjectSpoolDashboardSaga);
  yield takeLatest(FETCH_ALL_SPOOLS, fetchAllSpoolsSaga);
  yield takeLatest(SAVE_SPOOL_FIT_UP, saveFitUpSaga);
  yield takeLatest(SAVE_SPOOL_PREHEAT, savePreheatSaga);
  yield takeLatest(SAVE_SPOOL_WELDING, saveWeldingSaga);
  yield takeLatest(SAVE_SPOOL_VT, saveVtSaga);
  yield takeLatest(SAVE_SPOOL_NDT, saveNdtSaga);
  yield takeLatest(SAVE_SPOOL_COATING, saveCoatingSaga);
  yield takeLatest(SAVE_SPOOL_ACCEPT, saveAcceptSaga);
  yield takeLatest(REGISTER_SPOOL_NCR, registerSpoolNcrSaga);
  yield takeLatest(FETCH_SPOOL_NCR_LIST, fetchSpoolNcrListSaga);
}



// Function to upload files to S3
export const getPreSignedUrlToUpload = async (fileList, payload, version = 1) => {
  try {
    const uploadedFiles = [];

    if (fileList && fileList.length > 0) {
      // Create file objects for all files
      const fileObjs = fileList.map(file => ({
        fileName: file.name.replace(/\(\d+\)/g, "").trim(), // Remove (1), (2), etc.
        contentType: file.type,
        projectId: payload.projectId,
        projectTypeId: payload.projectTypeId,
        pipeId: payload.pipeId,
        spoolId: payload.spoolId,
        type: payload.type,
        fileSize: file.size, // Use actual file size
        version: version || 1
      }));


      // Get signed URLs for all files at once
      const response = await axiosConfig.post(`/spool-tracker/getPresignedUrlToUploadSpoolsbaseDoc`, fileObjs);

      if (response.status === 200) {
        const presignedData = response.data.data;

        // Process each presigned URL
        for (const data of presignedData) {
          const file = fileList.find(f => f.name.replace(/\(\d+\)/g, "").trim() === data.fileName);

          if (file) {

            // Validate presigned URL data
            if (!data.presignedUrl) {
              Notification.error('Upload Failed', 'Failed to get upload URL from server.');
              continue; // Skip this file and continue with next
            }

            try {
              // Log the presigned URL (remove sensitive parts for security)
              const urlParts = data.presignedUrl.split('?');


              // Small delay to ensure URL is ready (optional)
              await new Promise(resolve => setTimeout(resolve, 100));

              // Upload file to S3
              const uploadResponse = await axios.put(data.presignedUrl, file, {
                headers: {
                  "Content-Type": file.type || "application/octet-stream",
                  // "Content-Disposition": `attachment; filename="${file.name}"`
                },
                // Add timeout to prevent hanging requests
               // timeout: 100000,
              });

              if (uploadResponse.status === 200) {
                uploadedFiles.push({ s3Key: data.s3Key, fileName: data.fileName });
              } else {
                Notification.error('File Upload Failed', `Failed to upload ${file.name}. Please try again.`);
              }
            } catch (error) {
              // Add more detailed error logging
              if (error.response) {
              }
              // Show user-friendly error
              Notification.error('File Upload Failed', `Failed to upload ${file.name}. Please try again.`);
            }
          } else {
            Notification.error('File Upload Failed', `File ${data.fileName} not found.`);
          }
        }
      } else {
        Notification.error('Upload Failed', 'Failed to get upload URLs from server.');
      }

    }
    return uploadedFiles;
  } catch (error) {
    const msg =
      get(error, 'response.data.message') ||
      get(error, 'response.data.data') ||
      get(error, 'message') ||
      'Presigned upload request failed';
    if (typeof msg === 'string' && msg.trim()) {
      Notification.error('Upload Failed', msg);
    }
    return []; // Return empty array on error
  }
};

/**
 * GET-style download URL for a document already stored under `s3Key`.
 * Backend: POST `/spool-tracker/getPresignedUrlToDownloadSpoolsbaseDoc`
 */
export const getPresignedUrlToDownloadSpoolsbaseDoc = async (params) => {
  try {
    const { s3Key, projectId, pipeId, spoolId, projectTypeId } = params || {};
    if (!s3Key || String(s3Key).trim() === '') return null;
    const body = [{ s3Key, projectId, pipeId, spoolId, projectTypeId }];
    const response = await axiosConfig.post(
      `/spool-tracker/getPresignedUrlToDownloadSpoolsbaseDoc`,
      body
    );
    if (response.status !== 200) return null;
    const data = response.data?.data;
    const row = Array.isArray(data) ? data[0] : data;
    return row?.presignedUrl || row?.downloadUrl || row?.url || null;
  } catch (error) {
    const msg =
      get(error, 'response.data.message') ||
      get(error, 'response.data.data') ||
      get(error, 'message') ||
      'Could not get download link';
    if (typeof msg === 'string' && msg.trim()) {
      Notification.error('Download', msg);
    }
    return null;
  }
};



export const generateQrCodeSpoolsbase = async (qrCodeTexts = []) => {
  try {
    const texts = qrCodeTexts.map((text) => String(text).trim()).filter(Boolean);
    if (texts.length === 0) {
      return [];
    }

    const baseUrl = 'https://uawu2oh2i6.execute-api.us-west-2.amazonaws.com/dev/generate';
    const bulkEndpoint = baseUrl.replace('/generate', '/generate-bulk');
    const apiKey = 'data360-GWjjPcCPYmyY0pzu';

    const qrPayload = {
      texts: texts.join(','),
      qr_code_type: 'Weld ID',
      qr_size: 500,
      error_correction: 'H',
      bottom_text: texts.join(','),
    };

    Object.keys(qrPayload).forEach(key => {
      if (qrPayload[key] === undefined || qrPayload[key] === '') {
        delete qrPayload[key];
      }
    });

    const qrResponse = await fetch(bulkEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(qrPayload)
    });

    const qrData = await qrResponse.json();

    if (qrResponse.ok && qrData.success && Array.isArray(qrData.images)) {
      const qrTextToImageMap = new Map();
      qrData.images.forEach((qrItem) => {
        if (qrItem?.text && qrItem?.image) {
          qrTextToImageMap.set(String(qrItem.text), qrItem.image);
        }
      });

      return texts
        .map((text) => {
          const image = qrTextToImageMap.get(text);
          return image ? { text, image } : null;
        })
        .filter(qr => qr !== null);
    }

    console.warn('⚠️ [Redux Saga] QR code generation failed:', qrData.detail || 'Unknown error');
    return [];
  } catch (error) {
    console.error('❌ [Redux Saga] Error generating QR codes:', error);
    return [];
  }
};